import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { sponsorService } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const EMPTY = { name: '', text: '', link_url: '', is_active: true, starts_at: '', ends_at: '', image: '', remove_image: false };
const MAX_KB = 400;

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

function Sponsors() {
  const { enqueueSnackbar } = useSnackbar();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null); // null = fermé ; objet = en cours d'édition
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setList((await sponsorService.getAll()).data.sponsors); }
    catch (e) { enqueueSnackbar(e.response?.data?.error || 'Chargement impossible', { variant: 'error' }); }
  }, [enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  const open = (s) => {
    setError('');
    setEditingId(s ? s.id : null);
    setForm(s ? { ...EMPTY, name: s.name, text: s.text || '', link_url: s.link_url || '', is_active: s.is_active, starts_at: s.starts_at || '', ends_at: s.ends_at || '' } : { ...EMPTY });
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickImage = async (file) => {
    if (!file) return;
    if (file.size > MAX_KB * 1024) { setError(`Image trop lourde (${MAX_KB} Ko maximum).`); return; }
    setError('');
    set('image', await readFile(file));
    set('remove_image', false);
  };

  const save = async () => {
    setError('');
    try {
      const body = { ...form, starts_at: form.starts_at || null, ends_at: form.ends_at || null };
      if (!body.image) delete body.image;
      if (editingId) await sponsorService.update(editingId, body); else await sponsorService.create(body);
      setForm(null);
      enqueueSnackbar('Parrain enregistré', { variant: 'success' });
      load();
    } catch (e) { setError(e.response?.data?.error || 'Enregistrement impossible'); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer le parrain « ${s.name} » ?`)) return;
    try { await sponsorService.remove(s.id); load(); }
    catch (e) { enqueueSnackbar(e.response?.data?.error || 'Suppression impossible', { variant: 'error' }); }
  };

  const status = (s) => {
    const d = new Date().toISOString().slice(0, 10);
    if (!s.is_active) return <Chip size="small" label="Désactivé" />;
    if (s.starts_at && s.starts_at > d) return <Chip size="small" color="info" label={`Commence le ${s.starts_at}`} />;
    if (s.ends_at && s.ends_at < d) return <Chip size="small" color="warning" label="Terminé" />;
    return <Chip size="small" color="success" label="Affiché" />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Parrains</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => open(null)} sx={{ textTransform: 'none', fontWeight: 700 }}>Ajouter un parrain</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Les parrains actifs s'affichent en bas des pages du portail public, sous « Avec le soutien de ». Aucun cookie : seuls le nombre d'affichages et de clics sont comptés.
      </Typography>

      <Card variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Parrain</TableCell><TableCell>État</TableCell><TableCell align="right">Affichages</TableCell><TableCell align="right">Clics</TableCell><TableCell align="right" /></TableRow>
          </TableHead>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {s.has_image && <Box component="img" src={`${API_URL}/sponsors/${s.id}/image`} alt="" sx={{ height: 36, maxWidth: 80, objectFit: 'contain' }} />}
                    <Box><Typography variant="body2" sx={{ fontWeight: 700 }}>{s.name}</Typography><Typography variant="caption" color="text.secondary">{s.text}</Typography></Box>
                  </Stack>
                </TableCell>
                <TableCell>{status(s)}</TableCell>
                <TableCell align="right">{s.views}</TableCell>
                <TableCell align="right">{s.clicks}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton size="small" onClick={() => open(s)} aria-label="Modifier"><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => remove(s)} aria-label="Supprimer"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 3 }}>Aucun parrain pour l'instant</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editingId ? 'Modifier le parrain' : 'Nouveau parrain'}</DialogTitle>
        <DialogContent dividers>
          {form && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {error && <Alert severity="warning">{error}</Alert>}
              <TextField label="Nom du parrain" value={form.name} onChange={(e) => set('name', e.target.value)} inputProps={{ maxLength: 80 }} required fullWidth />
              <TextField label="Phrase courte (facultatif)" value={form.text} onChange={(e) => set('text', e.target.value)} inputProps={{ maxLength: 160 }} helperText={`${form.text.length}/160`} fullWidth />
              <TextField label="Lien vers son site ou sa page (facultatif)" value={form.link_url} onChange={(e) => set('link_url', e.target.value)} placeholder="https://..." fullWidth />
              <Box>
                <Button component="label" variant="outlined" size="small" sx={{ textTransform: 'none' }}>
                  {form.image ? 'Changer le logo' : editingId ? 'Remplacer le logo' : 'Choisir un logo'}
                  <input type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={(e) => pickImage(e.target.files[0])} />
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>PNG, JPEG ou WebP, {MAX_KB} Ko maximum</Typography>
                {form.image && <Box component="img" src={form.image} alt="" sx={{ display: 'block', mt: 1, maxHeight: 70, maxWidth: '100%', objectFit: 'contain' }} />}
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Début (facultatif)" type="date" value={form.starts_at} onChange={(e) => set('starts_at', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
                <TextField label="Fin (facultatif)" type="date" value={form.ends_at} onChange={(e) => set('ends_at', e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
              </Stack>
              <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />} label="Affiché sur le site" />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Annuler</Button>
          <Button variant="contained" onClick={save} disabled={!form || !form.name.trim()}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Sponsors;
